import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../utils/apiClient';
import { FaLock, FaArrowLeft, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import config from '../config';

const ResetPasswordConfirm = ({ onLoginClick, isDarkMode }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Extract token from URL on load
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) {
      setToken(urlToken);
    } else {
      setError("Password reset token is missing from the URL.");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!token) {
      setError("Token is invalid or missing.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post(`${config.API_BASE_URL}/auth/password/reset/confirm`, {
        token,
        new_password: password,
        confirm_new_password: confirmPassword,
      });

      setMessage("Your password has been successfully reset! You can now log in.");
    } catch (err) {
      const detail = err?.response?.data?.detail;

      if (Array.isArray(detail)) {
        // Convert array of validation errors into a readable message
        const messages = detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
        setError(messages);
      } else if (typeof detail === 'object') {
        setError(detail.msg || JSON.stringify(detail));
      } else {
        setError(detail || "Failed to reset password. The token may have expired or is invalid.");
      }
    } finally {
      setLoading(false);
    }
  };

  const c = {
    card: isDarkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/95 border-gray-200',
    inputBg: isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900',
    text: isDarkMode ? 'text-gray-300' : 'text-gray-700',
    button: isDarkMode ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-indigo-700 text-white hover:bg-indigo-600',
  };

  return (
    <div className="flex justify-center items-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className={`w-full max-w-md p-8 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.15)] backdrop-blur ${c.card}`}
      >
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold">
            <span
              className={`bg-clip-text text-transparent bg-gradient-to-r ${
                isDarkMode ? 'from-indigo-300 to-blue-400' : 'from-indigo-600 to-blue-700'
              }`}
            >
              Set New Password
            </span>
          </h2>
          <p className={`${c.text} mt-2 text-sm`}>
            {token ? "Enter and confirm your new password below." : "Awaiting valid token..."}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-3 mb-4 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-3 text-sm"
          >
            <FaExclamationTriangle className="text-xl" />
            <p className="font-medium">
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </p>
          </motion.div>
        )}

        {/* Success Message */}
        {message ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 mb-4 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center gap-3"
          >
            <FaCheckCircle className="text-xl" />
            <p className="text-sm font-medium">{message}</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* New Password */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${c.text}`}>New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  required
                  disabled={!token || loading}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all focus:ring-indigo-400 focus:border-indigo-400 ${c.inputBg}`}
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${c.text}`}>Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="********"
                  required
                  disabled={!token || loading}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all focus:ring-indigo-400 focus:border-indigo-400 ${c.inputBg}`}
                />
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={loading || !token}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${c.button} ${loading || !token ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {loading ? 'Processing...' : 'Change Password'}
            </motion.button>
          </form>
        )}

        {/* Back to Login */}
        <div className="text-center mt-6">
          <motion.button
            onClick={onLoginClick}
            className={`flex items-center mx-auto text-sm gap-2 ${c.text} hover:text-indigo-500 transition-colors bg-transparent border-none p-0 cursor-pointer`}
            whileHover={{ x: -2 }}
          >
            <FaArrowLeft /> Back to Login
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordConfirm;
